import {observer} from 'mobx-react-lite'
import React from 'react'
import {StickerStateId} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Icon} from '@components/common/base/icon'
import {Sticker} from '@components/common/base/sticker/sticker'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {DraggableNative} from '@components/draggable-native'
import {TwStickerMenu} from '@components/sticker/sticker-menu/containers'
import {StickerMenuHeader} from '@components/sticker/sticker-menu/sticker-menu-header'
import {
  TwStickerItemState,
  TwStickerItemStateSticker,
  TwStickerMenuBody
} from '@components/sticker/sticker-menu/sticker-menu-string/containers'
import {
  getStateColor,
  getStatesList
} from '@components/sticker/sticker-menu/sticker-menu-string/utils'
import {useStickerStore} from '@components/sticker/sticker-store'
import {SprintCtl} from '@extensions/sticker/controllers/sprint-ctl'

export const StickerMenuStringTask = observer(() => {
  const [state, store] = useStickerStore()
  const stickerValue = state.value as string
  const allStates = getStatesList(store.sticker)
  const isSprintSticker = store.stickerData.stickerType === 'sprint'

  const canEdit =
    !store.stickerTask ||
    PermissionsChecker.checkEditTaskStickers(
      store.stickerTask,
      store.sticker,
      undefined
    )

  const onSelectState = (stateId: StickerStateId) => {
    if (canEdit) {
      store.setStickerValue(stateId)
      store.pinStickerValue()
      store.closeStickerMenu()
    }
  }

  return (
    <TwStickerMenu className="min-w-[280px]">
      <StickerMenuHeader
        stickerId={store.sticker.id}
        title={store.sticker.title}
        className="px-12 pt-4 pb-8"
        showUnpin={canEdit}
      />
      <WithPerfectScrollbar
        className="max-h-250 perfect-scrollbar-with-custom-scroll sticker-menu"
        wheelPropagation
        wheelSpeed={1.5}
      >
        <TwStickerMenuBody>
          {allStates.map(stickerState => {
            const isActive = stickerValue === stickerState.id
            const value = store.sticker.states.index[stickerState.id]
            const stateColor = value
              ? getStateColor(value, store.stickerData.stickerType)
              : 'sticker-gray'

            return (
              <TwStickerItemState
                key={stickerState.id}
                className="sticker-group"
                onClick={() => canEdit && onSelectState(stickerState.id)}
                style={!canEdit ? {opacity: 0.5, pointerEvents: 'none'} : {}}
              >
                <TwStickerItemStateSticker>
                  <DraggableNative
                    draggable={canEdit}
                    onDragStarted={store.startDragging}
                    onDragStartedCallback={store.closeStickerMenu}
                    onDragEnded={store.endDragging}
                    transfer={{
                      type: 'sticker',
                      stickerId: store.sticker.id,
                      value: stickerState.id
                    }}
                  >
                    <Sticker
                      icon={isSprintSticker ? '' : store.stickerData.icon}
                      color={stateColor}
                      iconColor={
                        isSprintSticker ? undefined : `icon-${stateColor}`
                      }
                      iconBackgroundColor={
                        isSprintSticker ? undefined : `icon-${stateColor}`
                      }
                      title={
                        <>
                          {stickerState.name}{' '}
                          {isSprintSticker && (
                            <span className="font-semibold">
                              {SprintCtl.formatInterval(stickerState)}
                            </span>
                          )}
                        </>
                      }
                    />
                  </DraggableNative>
                  <div className="flex items-center">
                    <Icon
                      icon="IconCustomCheck"
                      color={isActive ? 'action-default' : 'secondary'}
                      className={
                        isActive ? 'block' : 'hidden group-hover:block'
                      }
                    />
                  </div>
                </TwStickerItemStateSticker>
              </TwStickerItemState>
            )
          })}
        </TwStickerMenuBody>
      </WithPerfectScrollbar>
    </TwStickerMenu>
  )
})
